/**
 * API Error Class
 *
 * Typed error class for API responses
 * Based on rule-frontend.md Section 6.2
 */

export interface ApiErrorParams {
  code: string
  message: string
  status: number
  requestId?: string
  details?: Record<string, unknown>
}

export class ApiError extends Error {
  public readonly code: string
  public readonly status: number
  public readonly requestId?: string
  public readonly details?: Record<string, unknown>

  constructor(params: ApiErrorParams) {
    super(params.message)
    this.name = 'ApiError'
    this.code = params.code
    this.status = params.status
    this.requestId = params.requestId
    this.details = params.details

    // Maintains proper stack trace for where error was thrown (V8 only)
    if ('captureStackTrace' in Error && typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(this, ApiError)
    }
  }

  /**
   * Check if this is an auth error (401)
   */
  get isAuthError(): boolean {
    return this.status === 401
  }

  /**
   * Check if this is a permission error (403)
   */
  get isPermissionError(): boolean {
    return this.status === 403
  }

  /**
   * Check if this is a PDPA violation
   */
  get isPdpaViolation(): boolean {
    return this.code === 'PDPA_VIOLATION' || this.code === 'AGGREGATION_TOO_SMALL'
  }

  /**
   * Check if this is a client error (4xx)
   */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500
  }

  /**
   * Check if this is a server error (5xx)
   */
  get isServerError(): boolean {
    return this.status >= 500
  }

  /**
   * Convert to plain object
   */
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      status: this.status,
      requestId: this.requestId,
      details: this.details,
    }
  }

  /**
   * Get user-friendly message
   *
   * Returns a message safe to display to users (no technical details)
   */
  getUserMessage(): string {
    // Use the message from backend (already user-friendly in Thai)
    return this.message
  }

  /**
   * Create ApiError from fetch Response
   *
   * Parses error response and creates typed ApiError
   */
  static async fromResponse(response: Response): Promise<ApiError> {
    try {
      const json = await response.json()

      return new ApiError({
        code: json.error?.code ?? 'UNKNOWN',
        message: json.error?.message ?? 'เกิดข้อผิดพลาด',
        status: response.status,
        requestId: json.requestId,
        details: json.error?.details,
      })
    } catch (e) {
      // Failed to parse JSON → return generic error
      return new ApiError({
        code: 'UNKNOWN',
        message: 'เกิดข้อผิดพลาดในการเชื่อมต่อ',
        status: response.status,
      })
    }
  }

  /**
   * Create ApiError for network errors (no response)
   */
  static networkError(): ApiError {
    return new ApiError({
      code: 'NETWORK_ERROR',
      message: 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต',
      status: 0,
    })
  }
}
